/**
 * ConfirmationModal - A reusable modal for confirmations, alerts, and user feedback
 * 
 * Replaces browser alert() and confirm() with a styled, mobile-friendly modal.
 * 
 * Features:
 * - Different variants: success, warning, error, info, confirm
 * - Mobile-optimized with large touch targets
 * - Slides up from bottom on mobile (sheet-style)
 * - Supports custom actions
 */

import React from 'react'
import { 
 X, CheckCircle2, AlertTriangle, XCircle, Info, 
 HelpCircle, Loader2 
} from 'lucide-react'

export type ModalVariant = 'success' | 'warning' | 'error' | 'info' | 'confirm'

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full'

export interface ConfirmationModalProps {
 isOpen: boolean
 onClose: () => void
 title: string
 message: React.ReactNode
 variant?: ModalVariant
 // For confirm dialogs
 onConfirm?: () => void
 confirmText?: string
 cancelText?: string
 // For alerts (single button)
 actionText?: string
 // Loading state
 isLoading?: boolean
 // Optional: don't show close button (for critical dialogs)
 hideCloseButton?: boolean
 // Optional: custom icon
 icon?: React.ReactNode
 // Size: sm (384px), md (448px), lg (512px), xl (576px)
 size?: ModalSize
}

const variantConfig = {
 success: {
 icon: CheckCircle2,
 iconColor: 'text-ok',
 bgColor: 'bg-tint-ok-soft',
 borderColor: 'border-tint-ok-edge',
 confirmBtnClass: 'bg-ok hover:bg-ok text-on-brand',
 },
 warning: {
 icon: AlertTriangle,
 iconColor: 'text-warn',
 bgColor: 'bg-tint-warn-soft',
 borderColor: 'border-tint-warn-edge',
 confirmBtnClass: 'bg-warn hover:bg-warn text-on-brand',
 },
 error: {
 icon: XCircle,
 iconColor: 'text-bad',
 bgColor: 'bg-tint-bad-soft',
 borderColor: 'border-tint-bad-edge',
 confirmBtnClass: 'bg-bad hover:bg-bad text-on-brand',
 },
 info: {
 icon: Info,
 iconColor: 'text-brand',
 bgColor: 'bg-brand-subtle',
 borderColor: 'border-brand-subtle',
 confirmBtnClass: 'bg-brand hover:bg-brand-hover text-on-brand',
 },
 confirm: {
 icon: HelpCircle,
 iconColor: 'text-info',
 bgColor: 'bg-tint-info-soft',
 borderColor: 'border-tint-info-edge ',
 confirmBtnClass: 'bg-info hover:bg-brand-hover text-on-brand',
 },
}

const sizeClasses: Record<ModalSize, string> = {
 sm: 'sm:max-w-sm', // 384px
 md: 'sm:max-w-md', // 448px
 lg: 'sm:max-w-lg', // 512px
 xl: 'sm:max-w-xl', // 576px
 full: 'sm:max-w-2xl max-h-[90vh]', // Near full-screen, good for mobile
}

export function ConfirmationModal({
 isOpen,
 onClose,
 title,
 message,
 variant = 'info',
 onConfirm,
 confirmText = 'Confirm',
 cancelText = 'Cancel',
 actionText = 'OK',
 isLoading = false,
 hideCloseButton = false,
 icon,
 size = 'md',
}: ConfirmationModalProps) {
 // 2026-08-29 UI audit: this is the app-wide replacement for native confirm(),
 // and it had none of the behaviour a native dialog gives you free — Escape did
 // nothing, Tab walked out of the dialog into the page behind the scrim, and
 // focus never came back to whatever opened it. All three below.
 const panelRef = React.useRef<HTMLDivElement>(null)
 const previousFocusRef = React.useRef<HTMLElement | null>(null)
 const onCloseRef = React.useRef(onClose)
 onCloseRef.current = onClose
 const isLoadingRef = React.useRef(isLoading)
 isLoadingRef.current = isLoading

 React.useEffect(() => {
 if (!isOpen) return

 previousFocusRef.current = document.activeElement as HTMLElement | null

 const focusables = () => Array.from(
 panelRef.current?.querySelectorAll<HTMLElement>(
 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
 ) ?? []
 )

 const handleKeyDown = (e: KeyboardEvent) => {
 if (e.key === 'Escape') {
 // A dialog mid-work is not cancellable; the same rule the backdrop uses.
 if (!isLoadingRef.current) {
 e.stopPropagation()
 onCloseRef.current()
 }
 return
 }
 if (e.key !== 'Tab') return
 const items = focusables()
 if (items.length === 0) return
 const first = items[0]
 const last = items[items.length - 1]
 const active = document.activeElement
 const inside = !!active && !!panelRef.current?.contains(active)
 if (e.shiftKey && (active === first || !inside)) {
 e.preventDefault()
 last.focus()
 } else if (!e.shiftKey && (active === last || !inside)) {
 e.preventDefault()
 first.focus()
 }
 }

 document.addEventListener('keydown', handleKeyDown)
 const raf = requestAnimationFrame(() => focusables()[0]?.focus())

 return () => {
 document.removeEventListener('keydown', handleKeyDown)
 cancelAnimationFrame(raf)
 // Focus restore: back to whatever opened the dialog.
 previousFocusRef.current?.focus?.()
 previousFocusRef.current = null
 }
 }, [isOpen])

 if (!isOpen) return null

 const config = variantConfig[variant]
 const IconComponent = config.icon
 const isConfirmDialog = variant === 'confirm' || !!onConfirm
 const sizeClass = sizeClasses[size]

 const handleConfirm = () => {
 if (onConfirm) {
 onConfirm()
 }
 if (!isLoading) {
 onClose()
 }
 }

 const handleBackdropClick = (e: React.MouseEvent) => {
 if (e.target === e.currentTarget && !isLoading) {
 onClose()
 }
 }

 return (
 <div 
 role="dialog"
 aria-modal="true"
 aria-labelledby="confirmation-modal-title"
 className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
 onClick={handleBackdropClick}
 >
 {/* Backdrop */}
 <div className="absolute inset-0 bg-overlay backdrop-blur-sm" aria-hidden="true" />
 
 {/* Modal - slides up on mobile, centered on desktop */}
 <div 
 ref={panelRef}
 className={`
 relative w-full ${sizeClass}
 bg-surface 
 rounded-t-2xl sm:rounded-2xl
 shadow-2xl
 transform transition-all duration-300 ease-out
 ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full sm:translate-y-4 opacity-0'}
 `}
 onClick={(e) => e.stopPropagation()}
 >
 {/* Header with icon */}
 <div className={`${config.bgColor} ${config.borderColor} border-b rounded-t-2xl sm:rounded-t-2xl px-6 py-5`}>
 <div className="flex items-start gap-4">
 {/* Icon */}
 <div className={`flex-shrink-0 ${config.iconColor}`}>
 {icon || <IconComponent className="w-8 h-8" />}
 </div>
 
 {/* Title and close button */}
 <div className="flex-1 min-w-0">
 <h3 id="confirmation-modal-title" className="text-lg font-semibold text-primary pr-8">
 {title}
 </h3>
 </div>
 
 {/* Close button */}
 {!hideCloseButton && !isLoading && (
 <button
 onClick={onClose}
 className="absolute top-4 right-4 min-h-touch min-w-touch flex items-center justify-center rounded-full hover:bg-surface-alt transition-colors"
 aria-label="Close"
 >
 <X className="w-5 h-5 text-muted" />
 </button>
 )}
 </div>
 </div>
 
 {/* Body */}
 <div className="px-6 py-5">
 <div className="text-secondary text-sm leading-relaxed">
 {typeof message === 'string' ? (
 <p className="whitespace-pre-line">{message}</p>
 ) : (
 message
 )}
 </div>
 </div>
 
 {/* Footer with buttons - large touch targets for mobile */}
 <div className="px-6 pb-6 pt-2">
 {isConfirmDialog ? (
 // Confirm dialog: Cancel + Confirm buttons
 <div className="flex flex-col-reverse sm:flex-row gap-3">
 <button
 onClick={onClose}
 disabled={isLoading}
 className={`
 flex-1 py-3.5 px-6 rounded-xl font-medium text-base
 bg-surface-alt 
 text-secondary
 hover:bg-surface-alt
 transition-colors
 disabled:opacity-50 disabled:cursor-not-allowed
 min-h-13
 `}
 >
 {cancelText}
 </button>
 <button
 onClick={handleConfirm}
 disabled={isLoading}
 className={`
 flex-1 py-3.5 px-6 rounded-xl font-medium text-base
 ${config.confirmBtnClass}
 transition-colors
 disabled:opacity-50 disabled:cursor-not-allowed
 flex items-center justify-center gap-2
 min-h-13
 `}
 >
 {isLoading ? (
 <>
 <Loader2 className="w-5 h-5 animate-spin" />
 Processing...
 </>
 ) : (
 confirmText
 )}
 </button>
 </div>
 ) : (
 // Alert dialog: single OK button
 <button
 onClick={onClose}
 disabled={isLoading}
 className={`
 w-full py-3.5 px-6 rounded-xl font-medium text-base
 ${config.confirmBtnClass}
 transition-colors
 disabled:opacity-50 disabled:cursor-not-allowed
 min-h-13
 `}
 >
 {actionText}
 </button>
 )}
 </div>
 
 {/* Safe area padding for iOS */}
 <div className="h-safe-area-inset-bottom sm:hidden" />
 </div>
 </div>
 )
}

/**
 * Hook for managing confirmation modal state
 */
export interface UseConfirmationModalReturn {
 isOpen: boolean
 modalProps: Omit<ConfirmationModalProps, 'isOpen' | 'onClose'>
 show: (props: Omit<ConfirmationModalProps, 'isOpen' | 'onClose'>) => Promise<boolean>
 alert: (title: string, message: React.ReactNode, variant?: ModalVariant, size?: ModalSize) => Promise<void>
 confirm: (title: string, message: React.ReactNode, options?: { confirmText?: string; cancelText?: string; size?: ModalSize; variant?: ModalVariant }) => Promise<boolean>
 close: () => void
}

export function useConfirmationModal(): UseConfirmationModalReturn {
 const [isOpen, setIsOpen] = React.useState(false)
 const [modalProps, setModalProps] = React.useState<Omit<ConfirmationModalProps, 'isOpen' | 'onClose'>>({
 title: '',
 message: '',
 })
 const resolveRef = React.useRef<((value: boolean) => void) | null>(null)

 const show = React.useCallback((props: Omit<ConfirmationModalProps, 'isOpen' | 'onClose'>) => {
 return new Promise<boolean>((resolve) => {
 resolveRef.current = resolve
 setModalProps(props)
 setIsOpen(true)
 })
 }, [])

 const alert = React.useCallback((title: string, message: React.ReactNode, variant: ModalVariant = 'info', size?: ModalSize) => {
 return new Promise<void>((resolve) => {
 resolveRef.current = () => { resolve(); return true }
 setModalProps({ title, message, variant, size })
 setIsOpen(true)
 })
 }, [])

 const confirm = React.useCallback((
 title: string,
 message: React.ReactNode,
 options?: { confirmText?: string; cancelText?: string; size?: ModalSize; variant?: ModalVariant }
 ) => {
 return new Promise<boolean>((resolve) => {
 resolveRef.current = resolve
 setModalProps({
 title,
 message,
 variant: options?.variant ?? 'confirm',
 confirmText: options?.confirmText,
 cancelText: options?.cancelText,
 size: options?.size,
 onConfirm: () => {
 resolve(true)
 setIsOpen(false)
 }
 })
 setIsOpen(true)
 })
 }, [])

 const close = React.useCallback(() => {
 if (resolveRef.current) {
 resolveRef.current(false)
 resolveRef.current = null
 }
 setIsOpen(false)
 }, [])

 return {
 isOpen,
 modalProps,
 show,
 alert,
 confirm,
 close,
 }
}

